import { collection, doc, setDoc, deleteDoc, onSnapshot, query, orderBy } from "firebase/firestore";
import { db } from "../lib/firebase";
import {
  DraftSection,
  LibraryAnswerMatch,
  RequirementMatchesResult,
  RequirementCategory,
} from "../types";
import { auth } from "../lib/firebase";
import { sanitizeForFirestore } from "../utils/firestoreUtils";

/**
 * Counts words in a string
 */
export function countWords(text: string): number {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).length;
}

/**
 * Counts [ACTION NEEDED: ...] placeholders
 */
export function countPlaceholders(text: string): number {
  if (!text) return 0;
  const matches = text.match(/\[ACTION NEEDED:[^\]]+\]/gi);
  return matches ? matches.length : 0;
}

/**
 * Subscribes to real-time draft sections for an opportunity
 */
export function subscribeToDraftSections(
  opportunityId: string,
  callback: (sections: DraftSection[]) => void,
  onError?: (err: Error) => void,
): () => void {
  const colRef = collection(db, "opportunities", opportunityId, "draft_sections");
  const q = query(colRef, orderBy("updatedAt", "desc"));

  return onSnapshot(
    q,
    (snapshot) => {
      const sections: DraftSection[] = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();
        const content = data.content || "";
        return {
          id: docSnap.id,
          opportunityId,
          requirementId: data.requirementId || "",
          title: data.title || "",
          content,
          status: data.status || "draft",
          matchedAnswerIds: Array.isArray(data.matchedAnswerIds) ? data.matchedAnswerIds : [],
          usedAnswerIds: Array.isArray(data.usedAnswerIds) ? data.usedAnswerIds : [],
          wordCount: typeof data.wordCount === "number" ? data.wordCount : countWords(content),
          characterCount:
            typeof data.characterCount === "number" ? data.characterCount : content.length,
          placeholdersCount:
            typeof data.placeholdersCount === "number"
              ? data.placeholdersCount
              : countPlaceholders(content),
          humanEdited: Boolean(data.humanEdited),
          updatedAt: data.updatedAt || new Date().toISOString(),
          updatedBy: data.updatedBy || "",
          updatedByEmail: data.updatedByEmail || "",
          sectionRef: data.sectionRef || undefined,
          category: (data.category as RequirementCategory) || undefined,
          assignedTo: data.assignedTo || undefined,
          assignedToEmail: data.assignedToEmail || undefined,
          reviewerName: data.reviewerName || undefined,
          reviewerEmail: data.reviewerEmail || undefined,
          targetWordCount:
            typeof data.targetWordCount === "number" ? data.targetWordCount : undefined,
          dueDate: data.dueDate || undefined,
          notes: data.notes || undefined,
        };
      });
      callback(sections);
    },
    (err) => {
      console.error(`Error subscribing to draft sections for ${opportunityId}:`, err);
      if (onError) onError(err);
    },
  );
}

export interface SaveDraftSectionInput {
  id?: string;
  requirementId: string;
  title: string;
  content: string;
  status: "draft" | "review" | "approved";
  matchedAnswerIds?: string[];
  usedAnswerIds?: string[];
  humanEdited?: boolean;
  sectionRef?: string;
  category?: RequirementCategory;
  assignedTo?: string;
  assignedToEmail?: string;
  reviewerName?: string;
  reviewerEmail?: string;
  targetWordCount?: number;
  dueDate?: string;
  notes?: string;
}

/**
 * Saves or updates a draft section
 */
export async function saveDraftSection(
  opportunityId: string,
  section: SaveDraftSectionInput,
  user?: { displayName?: string | null; email?: string | null },
): Promise<string> {
  const colRef = collection(db, "opportunities", opportunityId, "draft_sections");
  const docRef = section.id ? doc(colRef, section.id) : doc(colRef);
  const now = new Date().toISOString();
  const content = section.content || "";

  const rawPayload: Omit<DraftSection, "id"> = {
    opportunityId,
    requirementId: section.requirementId,
    title: section.title,
    content,
    status: section.status,
    matchedAnswerIds: section.matchedAnswerIds || [],
    usedAnswerIds: section.usedAnswerIds || [],
    wordCount: countWords(content),
    characterCount: content.length,
    placeholdersCount: countPlaceholders(content),
    humanEdited: Boolean(section.humanEdited),
    updatedAt: now,
    updatedBy: user?.displayName || user?.email || "User",
    updatedByEmail: user?.email || "",
    sectionRef: section.sectionRef,
    category: section.category,
    assignedTo: section.assignedTo,
    assignedToEmail: section.assignedToEmail,
    reviewerName: section.reviewerName,
    reviewerEmail: section.reviewerEmail,
    targetWordCount: section.targetWordCount,
    dueDate: section.dueDate,
    notes: section.notes,
  };

  const payload = sanitizeForFirestore(rawPayload);
  await setDoc(docRef, payload, { merge: true });
  return docRef.id;
}

/**
 * Deletes a draft section
 */
export async function deleteDraftSection(opportunityId: string, sectionId: string): Promise<void> {
  const docRef = doc(db, "opportunities", opportunityId, "draft_sections", sectionId);
  await deleteDoc(docRef);
}

/**
 * Invokes the phq_matchLibraryAnswers Cloud Function
 */
export async function matchLibraryAnswers(params: {
  opportunityId: string;
  requirementId?: string;
  minScoreThreshold?: number;
  maxMatchesPerRequirement?: number;
}): Promise<{
  results: RequirementMatchesResult[];
  totalRequirementsMatched: number;
  totalAnswersEvaluated: number;
}> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("You must be logged in to match Answer Library responses.");
  }
  const token = await currentUser.getIdToken();

  const baseUrl =
    import.meta.env.VITE_FUNCTIONS_BASE_URL ||
    "https://us-central1-in-house-dev-mlc.cloudfunctions.net";
  const url = `${baseUrl.replace(/\/$/, "")}/phq_matchLibraryAnswers`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(params),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || `Matching request failed with status ${response.status}`);
  }

  const data = await response.json();
  return {
    results: Array.isArray(data.results) ? data.results : [],
    totalRequirementsMatched: data.totalRequirementsMatched || 0,
    totalAnswersEvaluated: data.totalAnswersEvaluated || 0,
  };
}

/**
 * Invokes the phq_generateDraftSection Cloud Function
 */
export async function generateAiDraftSection(params: {
  opportunityId: string;
  requirementId: string;
  requirementTitle: string;
  requirementPrompt: string;
  requirementCategory?: string;
  clientName?: string;
  opportunityTitle?: string;
  matchedAnswers: LibraryAnswerMatch[];
  customInstructions?: string;
}): Promise<{
  text: string;
  wordCount: number;
  characterCount: number;
  answersUsed?: string[];
  placeholdersDetected: string[];
  modelUsed: string;
}> {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new Error("You must be logged in to generate draft narratives.");
  }
  const token = await currentUser.getIdToken();

  const baseUrl =
    import.meta.env.VITE_FUNCTIONS_BASE_URL ||
    "https://us-central1-in-house-dev-mlc.cloudfunctions.net";
  const url = `${baseUrl.replace(/\/$/, "")}/phq_generateDraftSection`;

  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      ...params,
      matchedAnswers: params.matchedAnswers.map((a) => ({
        id: a.answerId,
        title: a.title,
        body: a.fullBody || a.snippet,
        serviceTags: a.serviceTags,
      })),
    }),
  });

  if (!response.ok) {
    const errorBody = await response.json().catch(() => ({}));
    throw new Error(errorBody.error || `Draft generation failed with status ${response.status}`);
  }

  return response.json();
}
